import type { AxiosResponse, AxiosError } from 'axios';

import { REPORT_PATH } from './clientTiming';

const getRequestUrl = (error: AxiosError): string => error.config?.url || 'unknown';

const isDictionaryRequest = (url: string): boolean => url.includes('/api/dictionary/');
const isCurrentUserRequest = (url: string): boolean => url.includes('/api/current_user');

const shouldSuppressStatusLog = (status: number, url: string): boolean => {
  if (status === 401) {
    return isCurrentUserRequest(url);
  }

  // Only suppress 404 for dictionary lookups (not found is normal)
  // Don't suppress for stories - that's a real error
  if (status === 404) {
    return isDictionaryRequest(url);
  }

  // Don't suppress 503 - service down is important to show
  // Never suppress 500+ errors - these indicate real problems

  return false;
};

const getFriendlyErrorMessage = (error: AxiosError): string | null => {
  const status = error.response?.status;

  if (status === 401) return 'Authentication required. Please log in again.';
  if (status === 403) return 'You do not have permission to access this resource.';
  if (status === 404) return 'The requested resource was not found.';
  if (status === 503) return 'Service temporarily unavailable.';
  if (status && status >= 500) return 'Server error. Please try again later or contact support.';
  if (error.code === 'ECONNABORTED') return 'Request timeout - please check your connection';
  if (error.code === 'ERR_NETWORK') return 'Network error - please check your internet connection';

  return null;
};

const getSafeErrorDetails = (error: AxiosError) => ({
  url: getRequestUrl(error),
  method: error.config?.method || 'unknown',
  baseURL: error.config?.baseURL || 'unknown',
  status: error.response?.status || 'unknown',
  statusText: error.response?.statusText || 'unknown',
  code: error.code || 'unknown',
  message: error.message || 'unknown',
  hasConfig: !!error.config,
  hasResponse: !!error.response,
});

interface ApiWithResponseInterceptor {
  interceptors: {
    response: {
      use: (
        onFulfilled: (response: AxiosResponse) => AxiosResponse,
        onRejected: (error: AxiosError) => Promise<never>
      ) => void;
    };
  };
}

export const setupErrorInterceptor = (api: ApiWithResponseInterceptor) => {
  api.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      // Check if this is an axios error
      if (!error.isAxiosError) {
        return Promise.reject(error);
      }

      // A search the caller abandoned is not a failure worth logging.
      if (error.code === 'ERR_CANCELED') {
        return Promise.reject(error);
      }

      // The fire-and-forget timing beacon drops its own failures; a dead API is already logged by the real requests.
      if (error.config?.url === REPORT_PATH) {
        return Promise.reject(error);
      }

      const status = error.response?.status;
      const requestUrl = getRequestUrl(error);
      const safeErrorDetails = getSafeErrorDetails(error);

      if (status && !shouldSuppressStatusLog(status, requestUrl)) {
        console.error('API request failed', safeErrorDetails);
      }

      if (!status && error.code) {
        console.error('API network failure', safeErrorDetails);
      }

      const message = getFriendlyErrorMessage(error);
      if (message) {
        error.message = message;
      }

      return Promise.reject(error);
    }
  );
};
